// 1. React Imports:
import React, { useState, useEffect, useCallback } from "react";

// 2. Third-Party Library Imports:
import { Button, Grid, CircularProgress, Backdrop } from "@mui/material";
import { useToast } from "contexts/ToastContext"; // Import the useToast hook
import { DateRange } from "react-date-range";
import { format, subDays } from "date-fns";
import { Popover } from "@mui/material";

// 3. Custom Hooks:
import { useDropzone } from "react-dropzone";

// 4. Component Imports:

// 6. Utility Functions or Constants:
import { upload, req } from "utils/api";

// 7. Assets:
import AddIcon from "@mui/icons-material/Add";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import LoaderIcon from "@mui/icons-material/Loop";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

// Import date range picker styles
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

export function FileManager() {
  /*--------------------------------------
		  State Variables and Hooks
	-------------------------------------*/
  const { showToast } = useToast(); // Get the showToast function from the context
  const [filesUploading, setFilesUploading] = useState(false);
  const [filesUploaded, setFilesUploaded] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [processedFiles, setProcessedFiles] = useState(0);
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    fileName: '',
    fileId: null
  });

  const open = Boolean(anchorEl);
  const dateRangePopoverId = open ? "date-range-popover" : undefined;
  const selectionRange = {
    startDate: startDate,
    endDate: endDate,
    key: "selection",
  };

  /*--------------------------------------
		  Derived Data and Functions
	  -------------------------------------*/

  const filesHtml = filesUploaded.map((file) => (
    <div key={file.id} className="flex items-center justify-between p-4 border-b">
      <div className="flex items-center">
        <div className="flex items-center justify-center w-10 h-10 mr-4 bg-gray-200 rounded-full">
          <FileIcon className="w-6 h-6" />
        </div>
        <div className="flex flex-col">
          <p className="text-lg font-medium">{file.file_name}</p>
          <p className="text-sm text-gray-500">
            Uploaded on {new Date(file.time_received).toLocaleString()}
          </p>
        </div>
      </div>
      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={() => setDeleteDialog({ 
          open: true, 
          fileName: file.file_name,
          fileId: file.id 
        })}
      >
        Delete
      </Button>
    </div>
  ));

  /*--------------------------------------
	  Utility Functions
	 -------------------------------------*/

  const processFolder = async (entry) => {
    const files = [];
    
    async function traverseFileTree(entry) {
      if (entry.isFile) {
        if (entry.name.toLowerCase().endsWith('.csv')) {
          const file = await new Promise((resolve) => entry.file(resolve));
          files.push(file);
        }
      } else if (entry.isDirectory) {
        const dirReader = entry.createReader();
        const entries = await new Promise((resolve) => {
          dirReader.readEntries((entries) => resolve(entries));
        });
        for (const childEntry of entries) {
          await traverseFileTree(childEntry);
        }
      }
    }
    
    await traverseFileTree(entry);
    return files;
  };

  const processZipFile = async (zipFile) => {
    // You'll need to add JSZip to your dependencies
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(zipFile);
    const csvFiles = [];
    
    await Promise.all(
      Object.keys(zip.files).map(async (filename) => {
        if (filename.toLowerCase().endsWith('.csv')) {
          const content = await zip.files[filename].async('blob');
          const file = new File([content], filename, { type: 'text/csv' });
          csvFiles.push(file);
        }
      })
    );
    
    return csvFiles;
  };

  const ZIP_SIZE_THRESHOLD = 50 * 1024 * 1024; // 50MB threshold (chosen arbitrarily), changes if we upload the zip vs individual files

  const onDrop = useCallback(async (acceptedFiles) => {
    setFilesUploading(true);
    setUploadProgress(0);
    setProcessedFiles(0);
    
    try {
      let csvFiles = [];
      let zipFiles = [];
      let successCount = 0;
      let failureCount = 0;
      
      for (const item of acceptedFiles) {
        console.log('Processing file:', item.name, 'Type:', item.type, 'Size:', item.size); // Debug log
        
        if (item.type === 'text/csv') {
          csvFiles.push(item);
        } else if (item.type === 'application/zip' || 
                   item.type === 'application/x-zip-compressed' ||
                   item.name.toLowerCase().endsWith('.zip')) {
          if (item.size > ZIP_SIZE_THRESHOLD) {
            // Large ZIP files will be uploaded directly
            zipFiles.push(item);
          } else {
            // Small ZIP files will be extracted client-side
            const extractedFiles = await processZipFile(item);
            csvFiles = [...csvFiles, ...extractedFiles];
          }
        } else if (item.type === '') {
          // Folder handling remains the same
          const entry = item.webkitGetAsEntry();
          if (entry) {
            const folderFiles = await processFolder(entry);
            csvFiles = [...csvFiles, ...folderFiles];
          }
        }
      }

      if (csvFiles.length === 0 && zipFiles.length === 0) {
        showToast("No valid files found to upload", "warning");
        setFilesUploading(false);
        return;
      }

      setTotalFiles(csvFiles.length + zipFiles.length);
      
      // Upload large ZIP files first
      for (const zipFile of zipFiles) {
        try {
          await upload("admin/upload_zip", zipFile);
          successCount++;
          setProcessedFiles(prev => prev + 1);
          setUploadProgress(prev => prev + (100 / (csvFiles.length + zipFiles.length)));
        } catch (error) {
          failureCount++;
          console.error(`Error uploading ${zipFile.name}:`, error);
          showToast(`Error uploading ${zipFile.name}`, "error");
        }
      }

      // Then upload individual CSV files in batches
      const batchSize = 5;
      for (let i = 0; i < csvFiles.length; i += batchSize) {
        const batch = csvFiles.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (file) => {
            try {
              await upload("admin/upload_data", file);
              successCount++;
              setProcessedFiles(prev => prev + 1);
              setUploadProgress((prev) => (prev + (100 / (csvFiles.length + zipFiles.length))));
            } catch (error) {
              failureCount++;
              console.error(`Error uploading ${file.name}:`, error);
              showToast(`Error uploading ${file.name}`, "error");
            }
          })
        );
      }

      // Show final summary message
      const totalFiles = csvFiles.length + zipFiles.length;
      if (successCount > 0 && failureCount > 0) {
        showToast(`Uploaded ${successCount} file${successCount !== 1 ? 's' : ''} successfully, ${failureCount} file${failureCount !== 1 ? 's' : ''} failed`, "warning");
      } else if (successCount > 0) {
        showToast(`Successfully uploaded ${successCount} file${successCount !== 1 ? 's' : ''}`, "success");
      } else if (failureCount > 0) {
        //if theres a message shot it otherwise
        showToast(`Failed to upload ${failureCount} file${failureCount !== 1 ? 's' : ''}`, "error");
      }

      getRecentUploads();
    } catch (error) {
      showToast("Error processing files", "error");
    } finally {
      setFilesUploading(false);
      setUploadProgress(0);
      setProcessedFiles(0);
      setTotalFiles(0);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    noClick: false,
    noKeyboard: false,
    accept: {
      'text/csv': ['.csv'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
      'application/octet-stream': ['.zip'],
      '': ['*'] // Allow folder selection
    }
  });

  /*--------------------------------------
		  Event Handlers
	-------------------------------------*/

  const handleOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleChangeDate = (ranges) => {
    const { startDate: newStartDate, endDate: newEndDate } = ranges.selection;
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleDelete = async () => {
    try {
      const response = await req("post", "admin/delete_file_upload", {
        fileName: deleteDialog.fileName
      });
      
      if (response.status === 200) {
        showToast("File deleted successfully", "success");
        getRecentUploads(); // Refresh the table
      }
    } catch (error) {
      showToast("Error deleting file", "error");
    } finally {
      setDeleteDialog({ open: false, fileName: '', fileId: null });
    }
  };

  const handleExport = async () => {
    try {
      const startTimestamp = format(startDate, 'yyyy-MM-dd');
      const endTimestamp = format(endDate, 'yyyy-MM-dd');
      
      const response = await req(
        "get", 
        `admin/get_recent_uploads?startDate=${startTimestamp}&endDate=${endTimestamp}`
      );
      
      if (response && response.length > 0) {
        // Create CSV content
        const csvContent = [
          ["File Name", "Upload Date"], // CSV header
          ...response.map(file => [
            file.file_name,
            new Date(file.time_received).toLocaleString()
          ])
        ].map(row => row.join(",")).join("\n");

        // Create and download the file
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `uploaded_files_${startTimestamp}_to_${endTimestamp}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        showToast("File list exported successfully", "success");
      } else {
        showToast("No files found to export", "warning");
      }
    } catch (error) {
      console.error("Export error:", error);
      showToast("Error exporting file list", "error");
    }
  };

  /*--------------------------------------
		  Effects
	-------------------------------------*/

  useEffect(() => {
    document.title = "File Manager";
    getRecentUploads();
  }, []);

  useEffect(() => {
    getRecentUploads();
  }, [startDate, endDate]);

  /*--------------------------------------
		  Async Functions
  -------------------------------------*/
  const getRecentUploads = async () => {
    //get the recent uploads with date range
    const startTimestamp = format(startDate, 'yyyy-MM-dd');
    const endTimestamp = format(endDate, 'yyyy-MM-dd');
    
    let uploads = await req("get", `admin/get_recent_uploads?startDate=${startTimestamp}&endDate=${endTimestamp}`);
    console.log("RecentUploads", uploads);
    setFilesUploaded(uploads);
  };

  return (
    <div id="files" className="flex flex-auto flex-col w-full relative">
      <div id="" className="flex flex-auto flex-col w-full relative">
        <div className="flex min-w-0 flex-auto flex-col dark:bg-transparent sm:absolute sm:inset-0 sm:overflow-hidden">
          <div className="bg-card flex flex-col items-start border-b p-6 dark:bg-transparent sm:flex-row sm:items-center sm:justify-between sm:py-12 md:px-8">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <h1 className="text-4xl font-extrabold leading-none tracking-tight">
                  File Manager
                </h1>
                <div className="subtitle">
                  Upload, Review & Manage Files
                </div>
              </Grid>
              <Grid item xs={12} md={6} className="flex flex-row justify-between md:justify-end">
                <div>
                  <Button
                    className="mdc-button mat-mdc-button mat-unthemed mat-mdc-button-base"
                    startIcon={<CalendarTodayIcon />}
                    disableElevation
                    onClick={handleOpenPopover}
                  >
                    {`${format(startDate, "MMM dd, yyyy")} - ${format(endDate, "MMM dd, yyyy")}`}
                  </Button>
                  <Popover
                    id={dateRangePopoverId}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClosePopover}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                  >
                    <DateRange
                      ranges={[selectionRange]}
                      onChange={handleChangeDate}
                      moveRangeOnFirstSelection={false}
                    />
                  </Popover>
                </div>
                <div className="flex space-x-2">
                  {filesUploading ? (
                    <div className="flex items-center justify-center">
                      <LoaderIcon className="w-6 h-6 animate-spin" />
                    </div>
                  ) : (
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Button
                        variant="contained"
                        className="mdc-button mdc-button--unelevated mat-mdc-unelevated-button mat-primary mat-mdc-button-base"
                        startIcon={<AddIcon />}
                        disableElevation
                      >
                        Upload File
                      </Button>
                    </div>
                  )}
                  <Button
                    variant="contained"
                    className="mdc-button mdc-button--unelevated mat-mdc-unelevated-button mat-primary mat-mdc-button-base"
                    startIcon={<DownloadIcon />}
                    disableElevation
                    onClick={handleExport}
                  >
                    Export File List
                  </Button>
                </div>
              </Grid>
            </Grid>
          </div>
          <div className="flex flex-auto overflow-hidden border-t">
            <div className="flex flex-auto flex-col overflow-hidden sm:mb-18 sm:overflow-y-auto">
              {filesHtml}
            </div>
          </div>
        </div>
      </div>
      
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={filesUploading}
      >
        <div className="flex flex-col items-center">
          <CircularProgress color="inherit" />
          <div className="mt-4 text-white">
            Uploading {processedFiles} of {totalFiles} files ({Math.round(uploadProgress)}%)
          </div>
        </div>
      </Backdrop>
      
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, fileName: '', fileId: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {deleteDialog.fileName} and all its associated deposits?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, fileName: '', fileId: null })}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
